import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    CircularProgress
} from '@mui/material';
import { useNotify, usePermissions } from 'react-admin';
import { PartialDaySelector } from './PartialDaySelector';
import { LeaveTypeSelector } from './LeaveTypeSelector';
import { NotifyOthersInput } from './NotifyOthersInput';
import { EmployeePicker } from './EmployeePicker';
import {
    getNextWorkday,
    getWorkdays,
    createTimeoffRequest,
    getCurrentUserInfo,
    Employee,
    NotifyRecipient,
} from '../dataProvider';
import { getDefaultNotifyRecipient } from '../departmentDefaults';

export const ApplyTimeoff: React.FC = () => {
    const navigate = useNavigate();
    const notify = useNotify();
    const { permissions } = usePermissions<string[]>();
    const [loading, setLoading] = useState(false);
    const [workDays, setWorkDays] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [onBehalfOf, setOnBehalfOf] = useState<Employee | null>(null);
    const onBehalfOfRef = useRef<Employee | null>(null);

    const canApplyOnBehalf = Array.isArray(permissions) && (
        permissions.includes('timeoff.create_on_behalf')
    );

    const handleOnBehalfOfChange = (emp: Employee | null) => {
        onBehalfOfRef.current = emp;
        setOnBehalfOf(emp);
    };

    const [formData, setFormData] = useState({
        leave_type: 'Annual' as 'Annual' | 'Unpaid',
        start_date: '',
        end_date: '',
        start_partial: 'Full' as 'Full' | 'FirstHalf' | 'SecondHalf',
        end_partial: 'Full' as 'Full' | 'FirstHalf' | 'SecondHalf',
        note: '',
        notify_others: [] as NotifyRecipient[]
    });

    // On mount, fetch the current user's department and pre-populate notify_others
    // with the department's default contact. The user may remove or add recipients.
    useEffect(() => {
        const fetchDepartmentDefault = async () => {
            try {
                const userInfo = await getCurrentUserInfo();
                const defaultRecipient = getDefaultNotifyRecipient(userInfo?.department);
                if (defaultRecipient) {
                    setFormData(prev => ({ ...prev, notify_others: [defaultRecipient] }));
                }
            } catch (err) {
                // Silently ignore — pre-population is best-effort
                console.error('Failed to fetch current user department:', err);
            }
        };
        fetchDepartmentDefault();
    }, []);

    // On mount (and when on_behalf_of changes), fetch the next workday and set both
    // start_date and end_date to that value so users don't have to manually pick the
    // most common choice. Falls back to empty strings if the API call fails.
    useEffect(() => {
        const fetchNextWorkday = async () => {
            try {
                const result = await getNextWorkday(onBehalfOf?.employee_id);
                setFormData(prev => ({ ...prev, start_date: result.date, end_date: result.date }));
            } catch (err) {
                // Silently fall back to empty strings on failure
                console.error('Failed to fetch next workday:', err);
            }
        };
        fetchNextWorkday();
    }, [onBehalfOf]);

    const calculateWorkDays = async () => {
        if (!formData.start_date || !formData.end_date) {
            setWorkDays(null);
            return;
        }

        // When start and end date are the same, the end_partial selector is disabled,
        // so we use start_partial as end_partial to ensure consistent calculation.
        const effectiveEndPartial = formData.start_date === formData.end_date
            ? formData.start_partial
            : formData.end_partial;

        try {
            const result = await getWorkdays({
                start: formData.start_date,
                end: formData.end_date,
                start_partial: formData.start_partial,
                end_partial: effectiveEndPartial,
                employee_id: onBehalfOf?.employee_id,
            });
            setWorkDays(result.work_days);
            setError(null);
        } catch (err) {
            console.error('Error calculating work days:', err);
            setWorkDays(null);
        }
    };

    React.useEffect(() => {
        calculateWorkDays();
    }, [formData.start_date, formData.end_date, formData.start_partial, formData.end_partial, onBehalfOf]);

    /**
     * Handles form submission.
     *
     * When `workDays` exceeds 10, a browser confirmation dialog is presented
     * so the employee can explicitly acknowledge the over-limit request before
     * it is sent to the server.  The `(exceeds maximum of 10 days)` warning
     * remains visible in the UI to keep the employee informed.
     *
     * @param e - The React form submission event.
     */
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // If the request exceeds the 10-day limit, ask the employee to confirm
        // before proceeding.  This replaces the previous hard-block (disabled
        // button) with an explicit acknowledgement step.
        if (workDays !== null && workDays > 10) {
            const confirmed = window.confirm(
                `This request spans ${workDays} work days, which exceeds the maximum of 10 days. Are you sure you want to submit?`
            );
            if (!confirmed) {
                return;
            }
        }

        setLoading(true);
        setError(null);

        try {
            const payload: Record<string, unknown> = {
                ...formData,
                // Serialize each recipient as its employee_id (for known employees)
                // or its email address (for external recipients with employee_id === null).
                notify_others: formData.notify_others.length > 0
                    ? JSON.stringify(formData.notify_others.map(r => r.employee_id ?? r.email))
                    : null
            };

            if (onBehalfOfRef.current) {
                payload.on_behalf_of = onBehalfOfRef.current.employee_id;
            }

            await createTimeoffRequest(payload);

            notify('Time-off request submitted successfully', { type: 'success' });
            navigate('/timeoff');
        } catch (err: any) {
            const errorMessage = err?.body?.message || err?.message || 'Failed to submit request';
            setError(errorMessage);
            notify(errorMessage, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <CardContent>
                <Typography variant="h5" gutterBottom>
                    Apply for Time Off
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Box display="flex" flexDirection="column" gap={3}>
                        {canApplyOnBehalf && (
                            <EmployeePicker
                                label="Apply on Behalf of (Optional)"
                                value={onBehalfOf}
                                onChange={handleOnBehalfOfChange}
                                disabled={loading}
                            />
                        )}

                        <LeaveTypeSelector
                            value={formData.leave_type}
                            onChange={(value) => setFormData({ ...formData, leave_type: value })}
                            disabled={loading}
                            employeeId={onBehalfOf ? onBehalfOf.employee_id : null}
                        />

                        <Box display="flex" gap={2}>
                            <TextField
                                label="Start Date"
                                type="date"
                                required
                                fullWidth
                                value={formData.start_date}
                                onChange={(e) => setFormData({ ...formData, start_date: e.target.value, end_date: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                disabled={loading}
                            />
                            <TextField
                                label="End Date"
                                type="date"
                                required
                                fullWidth
                                value={formData.end_date}
                                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                disabled={loading}
                            />
                        </Box>

                        <Box display="flex" gap={2}>
                            <PartialDaySelector
                                label="Start Day"
                                value={formData.start_partial}
                                onChange={(value) => setFormData({ ...formData, start_partial: value })}
                                disabled={loading}
                            />
                            {/* When start and end are the same date, end_partial is irrelevant — disable it */}
                            <PartialDaySelector
                                label="End Day"
                                value={formData.end_partial}
                                onChange={(value) => setFormData({ ...formData, end_partial: value })}
                                disabled={loading || formData.start_date === formData.end_date}
                            />
                        </Box>

                        {/* Show an error alert when the selected dates/options yield zero work days */}
                        {workDays === 0 && (
                            <Alert severity="error">
                                The selected dates and options result in 0 work days. Please adjust your selection before submitting.
                            </Alert>
                        )}

                        {/* Show a warning when the request exceeds the 10-day limit */}
                        {workDays !== null && workDays > 10 && (
                            <Alert severity="warning">
                                Total work days: {workDays} (exceeds maximum of 10 days)
                            </Alert>
                        )}

                        {/* Show informational work day count for valid, non-zero requests */}
                        {workDays !== null && workDays > 0 && workDays <= 10 && (
                            <Alert severity="info">
                                Total work days: {workDays}
                            </Alert>
                        )}

                        <TextField
                            label="Note (Optional)"
                            multiline
                            rows={3}
                            fullWidth
                            value={formData.note}
                            onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                            disabled={loading}
                        />

                        <NotifyOthersInput
                            value={formData.notify_others}
                            onChange={(value) => setFormData({ ...formData, notify_others: value })}
                            disabled={loading}
                        />

                        <Box display="flex" gap={2} justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/timeoff')}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                // Disable when loading, dates are missing, work days are null (still
                                // calculating), or work days are 0 (invalid selection).
                                // Note: when work days exceed 10 the button remains enabled; a confirm
                                // dialog is shown in handleSubmit so the employee can acknowledge the
                                // over-limit request before it is sent.
                                disabled={loading || !formData.start_date || !formData.end_date || workDays === null || workDays === 0}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Submit Request'}
                            </Button>
                        </Box>
                    </Box>
                </form>
            </CardContent>
        </Card>
    );
};
